import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  type UserCredential
} from 'firebase/auth'
import { getFirebaseAuth } from '@/config'
import api from './api'
import type { User } from '@/types'

class AuthService {
  async register(email: string, password: string): Promise<User> {
    const auth = getFirebaseAuth()
    const userCredential: UserCredential = await createUserWithEmailAndPassword(auth, email, password)
    const idToken = await userCredential.user.getIdToken()
    
    const response = await api.post<User>('/me', { idToken })
    return response.data
  }

  async login(email: string, password: string): Promise<User> {
    const auth = getFirebaseAuth()
    const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password)
    const idToken = await userCredential.user.getIdToken()
    
    const response = await api.get<User>('/me', {
      headers: { Authorization: `Bearer ${idToken}` }
    })
    return response.data
  }

  async socialLogin(provider: 'google' | 'facebook'): Promise<User> {
    const auth = getFirebaseAuth()
    const authProvider = provider === 'google' 
      ? new GoogleAuthProvider() 
      : new FacebookAuthProvider()
    
    const userCredential: UserCredential = await signInWithPopup(auth, authProvider)
    const idToken = await userCredential.user.getIdToken()
    
    const response = await api.post<User>('/me', { idToken })
    return response.data
  }

  async logout(): Promise<void> {
    const auth = getFirebaseAuth()
    await auth.signOut()
    localStorage.clear()
  }

  async sendPasswordReset(email: string): Promise<void> {
    const auth = getFirebaseAuth()
    await sendPasswordResetEmail(auth, email)
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    const auth = getFirebaseAuth()
    await confirmPasswordReset(auth, code, newPassword)
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    const auth = getFirebaseAuth()
    const user = auth.currentUser
    if (!user || !user.email) throw new Error('No user logged in')
    
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)
    await updatePassword(user, newPassword)
  }

  async deleteAccount(password: string): Promise<void> {
    const auth = getFirebaseAuth()
    const user = auth.currentUser
    if (!user || !user.email) throw new Error('No user logged in')
    
    const credential = EmailAuthProvider.credential(user.email, password)
    await reauthenticateWithCredential(user, credential)
    await api.delete('/me')
    await deleteUser(user)
  }

  async getIdToken(): Promise<string | null> {
    const auth = getFirebaseAuth()
    const user = auth.currentUser
    if (!user) return null
    return await user.getIdToken()
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get<User>('/me')
      return response.data
    } catch {
      return null
    }
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.patch<User>('/me', data)
    return response.data
  }
}

export const authService = new AuthService()
